import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Plus, Search, AlertTriangle, CircleAlert, Flame, ShieldAlert,
  Camera, ImagePlus, MessageSquare, X as XIcon, Trash2, MapPin,
  CheckCircle2, Clock3, Wrench, Eye, Check, Sparkles, Copy,
} from "lucide-react";
import { toast } from "sonner";

// ---------- Meta ----------
const SEVERITIES = [
  { key: "low",      label: "Low",      color: "#64748B", icon: CircleAlert,   bg: "bg-slate-500/10  text-slate-500  border-slate-500/40"  },
  { key: "medium",   label: "Medium",   color: "#F59E0B", icon: AlertTriangle, bg: "bg-amber-500/10  text-amber-600  border-amber-500/40"  },
  { key: "high",     label: "High",     color: "#F97316", icon: ShieldAlert,   bg: "bg-orange-500/10 text-orange-600 border-orange-500/40" },
  { key: "critical", label: "Critical", color: "#EF4444", icon: Flame,         bg: "bg-rose-500/10   text-rose-500   border-rose-500/40"   },
];
const SEV_MAP = Object.fromEntries(SEVERITIES.map((s) => [s.key, s]));

const STATUSES = [
  { key: "open",        label: "Open",        color: "#F97316", icon: AlertTriangle,  next: "in_progress" },
  { key: "in_progress", label: "In Progress", color: "#3B82F6", icon: Clock3,         next: "fixed" },
  { key: "fixed",       label: "Fixed",       color: "#8B5CF6", icon: Wrench,         next: "verified" },
  { key: "verified",    label: "Verified",    color: "#10B981", icon: Eye,            next: "closed" },
  { key: "closed",      label: "Closed",      color: "#64748B", icon: CheckCircle2,   next: null },
];
const STATUS_MAP = Object.fromEntries(STATUSES.map((s) => [s.key, s]));

const fmtDate = (s) => {
  if (!s) return "—";
  try { return new Date(s).toLocaleDateString("en-IN", { day: "2-digit", month: "short" }); }
  catch { return s; }
};

const isOverdue = (s) => {
  if (!s.due_date) return false;
  if (["closed", "verified"].includes(s.status)) return false;
  return new Date(s.due_date) < new Date();
};

// ---------- Main Tab ----------
export default function SnagsTab({ pid, project, user }) {
  const [snags, setSnags] = useState([]);
  const [summary, setSummary] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterSeverity, setFilterSeverity] = useState("all");
  const [creating, setCreating] = useState(false);
  const [openSnag, setOpenSnag] = useState(null);
  const [showAISummary, setShowAISummary] = useState(false);

  const memberOf = useCallback((id) => members.find((m) => m.id === id), [members]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, sum, m] = await Promise.all([
        api.get(`/projects/${pid}/snags`),
        api.get(`/projects/${pid}/snags/summary`),
        api.get("/employees").catch(() => ({ data: [] })),
      ]);
      setSnags(s.data || []);
      setSummary(sum.data || null);
      setMembers(m.data || []);
    } catch (e) { toast.error("Failed to load snags"); }
    finally { setLoading(false); }
  }, [pid]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    return snags.filter((s) => {
      if (filterStatus !== "all" && s.status !== filterStatus) return false;
      if (filterSeverity !== "all" && s.severity !== filterSeverity) return false;
      if (q.trim()) {
        const needle = q.toLowerCase();
        if (!(s.title?.toLowerCase().includes(needle)
          || s.snag_no?.toLowerCase().includes(needle)
          || s.location?.toLowerCase().includes(needle)
          || s.description?.toLowerCase().includes(needle))) return false;
      }
      return true;
    });
  }, [snags, q, filterStatus, filterSeverity]);

  return (
    <div className="space-y-4" data-testid="snags-tab">
      {/* Summary strip */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
          <SummaryCard label="Total"      value={summary.total}   accent="text-foreground" />
          <SummaryCard label="Overdue"    value={summary.overdue} accent={summary.overdue > 0 ? "text-rose-500" : "text-muted-foreground"} />
          {STATUSES.slice(0, 4).map((st) => (
            <SummaryCard key={st.key} label={st.label} value={summary.by_status?.[st.key] || 0} accent="" color={st.color} />
          ))}
        </div>
      )}

      {/* Toolbar */}
      <div className="rounded-xl border border-border/60 bg-card px-3 py-2 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search snag no, title, room…" className="pl-8 h-8 text-xs" data-testid="snag-search" />
        </div>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="h-8 text-xs w-36" data-testid="snag-filter-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {STATUSES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterSeverity} onValueChange={setFilterSeverity}>
          <SelectTrigger className="h-8 text-xs w-36" data-testid="snag-filter-severity"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All severities</SelectItem>
            {SEVERITIES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button size="sm" onClick={() => setCreating(true)} className="bg-primary text-primary-foreground" data-testid="snag-new">
          <Plus className="w-3.5 h-3.5 mr-1" /> New Snag
        </Button>
        <Button size="sm" variant="outline" onClick={() => setShowAISummary(true)} disabled={snags.length === 0} data-testid="snag-ai-summary">
          <Sparkles className="w-3.5 h-3.5 mr-1 text-primary" /> AI Summary
        </Button>
      </div>

      {/* Body */}
      {loading && <div className="rounded-2xl border border-border/60 bg-card p-16 text-center text-muted-foreground">Loading snags…</div>}
      {!loading && filtered.length === 0 && (
        <div className="rounded-2xl border border-dashed border-border/70 bg-muted/10 p-14 text-center" data-testid="snags-empty">
          <AlertTriangle className="w-6 h-6 text-primary mx-auto mb-2" />
          <div className="text-sm font-medium mb-1">No snags {snags.length > 0 ? "match your filters" : "yet"}</div>
          <div className="text-xs text-muted-foreground mb-4">
            {snags.length > 0 ? "Try clearing the search or filters." : "Log defects and punch-list items so your team can close them out before handover."}
          </div>
          {snags.length === 0 && <Button size="sm" onClick={() => setCreating(true)} className="bg-primary text-primary-foreground">Log first snag</Button>}
        </div>
      )}
      {!loading && filtered.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="snags-grid">
          {filtered.map((s) => (
            <SnagCard key={s.id} snag={s} memberOf={memberOf} onOpen={() => setOpenSnag(s)} />
          ))}
        </div>
      )}

      {/* Dialogs */}
      <SnagCreateDialog
        open={creating}
        pid={pid}
        members={members}
        onClose={() => setCreating(false)}
        onSaved={() => { setCreating(false); load(); }}
      />
      <SnagDetailDialog
        open={!!openSnag}
        snagId={openSnag?.id}
        pid={pid}
        members={members}
        user={user}
        onClose={() => setOpenSnag(null)}
        onChanged={load}
      />
      <SnagAISummaryDialog
        open={showAISummary}
        pid={pid}
        project={project}
        snagCount={snags.length}
        onClose={() => setShowAISummary(false)}
      />
    </div>
  );
}

// ---------- AI Summary dialog ----------
function SnagAISummaryDialog({ open, pid, project, snagCount, onClose }) {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSummary(null); setShowHistory(false);
    api.get(`/ai/snag-summary/${pid}/history`).then((r) => setHistory(r.data || [])).catch(() => setHistory([]));
  }, [open, pid]);

  const generate = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/ai/snag-summary", { project_id: pid });
      setSummary(data);
      const h = await api.get(`/ai/snag-summary/${pid}/history`);
      setHistory(h.data || []);
      toast.success("Summary generated");
    } catch (e) { toast.error(e.response?.data?.detail?.detail || "Failed to generate summary"); }
    finally { setLoading(false); }
  };

  const copy = () => {
    if (!summary) return;
    navigator.clipboard.writeText(summary.summary);
    toast.success("Copied to clipboard");
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-primary" /> AI Snag Briefing</DialogTitle>
          <DialogDescription>Executive summary of all {snagCount} snag{snagCount !== 1 ? "s" : ""} in {project?.name || "this project"} — powered by Gemma 4 31B.</DialogDescription>
        </DialogHeader>
        {!summary && !loading && (
          <div className="text-center py-8 space-y-3">
            <div className="w-14 h-14 mx-auto rounded-full bg-primary/10 flex items-center justify-center">
              <Sparkles className="w-6 h-6 text-primary" />
            </div>
            <div className="text-sm">
              Click below and Gemma will read every open, in-progress, fixed, and verified snag on this project — including severity, ownership, and due dates — and produce a director-ready briefing.
            </div>
            <Button onClick={generate} className="bg-primary text-primary-foreground" data-testid="snag-ai-generate">
              <Sparkles className="w-3.5 h-3.5 mr-1" /> Generate briefing
            </Button>
            {history.length > 0 && (
              <button onClick={() => setShowHistory((s) => !s)} className="text-xs text-muted-foreground hover:text-foreground underline pt-1">
                {showHistory ? "Hide" : "View"} last {history.length} briefing{history.length > 1 ? "s" : ""}
              </button>
            )}
          </div>
        )}
        {loading && (
          <div className="text-center py-12">
            <div className="w-8 h-8 mx-auto rounded-full border-2 border-primary border-t-transparent animate-spin mb-3" />
            <div className="text-xs text-muted-foreground">Reading snags and drafting…</div>
          </div>
        )}
        {summary && (
          <div className="space-y-3">
            <div className="rounded-lg border border-border/60 bg-muted/20 p-4 text-sm whitespace-pre-wrap" data-testid="snag-ai-output">
              {summary.summary}
            </div>
            <div className="flex items-center justify-between gap-2">
              <div className="text-[10px] text-muted-foreground">
                {summary.snag_count} snags · {summary.usage?.total_tokens || 0} tokens · {new Date(summary.generated_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={copy}><Copy className="w-3.5 h-3.5 mr-1" />Copy</Button>
                <Button size="sm" onClick={generate} className="bg-primary text-primary-foreground"><Sparkles className="w-3.5 h-3.5 mr-1" />Regenerate</Button>
              </div>
            </div>
          </div>
        )}
        {showHistory && history.length > 0 && (
          <div className="mt-4 border-t border-border/40 pt-3 space-y-2">
            <div className="text-xs uppercase tracking-widest text-muted-foreground">History</div>
            {history.map((h) => (
              <button key={h.id} onClick={() => setSummary(h)} className="w-full text-left rounded border border-border/50 hover:border-primary/40 hover:bg-muted/30 p-2 text-xs" data-testid={`snag-ai-hist-${h.id}`}>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">{new Date(h.generated_at).toLocaleString("en-IN")}</span>
                  <span className="text-muted-foreground">{h.snag_count} snags</span>
                </div>
                <div className="truncate mt-1">{h.summary?.split("\n")[0]}</div>
              </button>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ---------- Sub-components ----------

function SummaryCard({ label, value, accent = "", color }) {
  return (
    <div className="rounded-xl border border-border/60 bg-card px-3 py-2">
      <div className="text-[9px] uppercase tracking-widest text-muted-foreground flex items-center gap-1">
        {color && <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: color }} />}
        {label}
      </div>
      <div className={`text-2xl font-semibold font-display mt-0.5 ${accent}`}>{value}</div>
    </div>
  );
}

function SeverityPill({ severity }) {
  const s = SEV_MAP[severity] || SEV_MAP.medium;
  const Icon = s.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded border ${s.bg}`} data-testid={`sev-pill-${severity}`}>
      <Icon className="w-2.5 h-2.5" /> {s.label}
    </span>
  );
}

function StatusPill({ status }) {
  const s = STATUS_MAP[status] || STATUS_MAP.open;
  const Icon = s.icon;
  return (
    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded border" style={{ color: s.color, borderColor: s.color + "55", background: s.color + "12" }}>
      <Icon className="w-2.5 h-2.5" /> {s.label}
    </span>
  );
}

function SnagCard({ snag, memberOf, onOpen }) {
  const overdue = isOverdue(snag);
  const owner = memberOf(snag.assigned_to);
  const thumb = snag.photo_count > 0;
  return (
    <button
      onClick={onOpen}
      data-testid={`snag-card-${snag.id}`}
      className={`text-left rounded-xl border bg-card overflow-hidden transition-all hover:shadow-lg hover:-translate-y-0.5 ${overdue ? "border-rose-500/50" : "border-border/60"}`}
    >
      <div className="p-3 space-y-2">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="text-[10px] font-mono text-muted-foreground uppercase tracking-widest">{snag.snag_no}</div>
            <div className="text-sm font-semibold truncate">{snag.title}</div>
            {snag.location && (
              <div className="text-[11px] text-muted-foreground truncate flex items-center gap-1 mt-0.5">
                <MapPin className="w-2.5 h-2.5" /> {snag.location}
              </div>
            )}
          </div>
          <SeverityPill severity={snag.severity} />
        </div>
        {snag.description && (
          <div className="text-[11px] text-muted-foreground line-clamp-2">{snag.description}</div>
        )}
        <div className="flex items-center justify-between gap-2 pt-1">
          <StatusPill status={snag.status} />
          <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
            {thumb && <span className="flex items-center gap-0.5"><Camera className="w-3 h-3" /> {snag.photo_count}</span>}
            {(snag.comment_count || 0) > 0 && <span className="flex items-center gap-0.5"><MessageSquare className="w-3 h-3" /> {snag.comment_count}</span>}
            <span className={`${overdue ? "text-rose-500 font-medium" : ""}`}>{snag.due_date ? fmtDate(snag.due_date) : "no due"}</span>
          </div>
        </div>
        {owner && (
          <div className="text-[10px] text-muted-foreground border-t border-border/40 pt-1.5 truncate">
            Assigned · {owner.name}
          </div>
        )}
      </div>
    </button>
  );
}

// ---------- Create dialog ----------
function SnagCreateDialog({ open, pid, members, onClose, onSaved }) {
  const [form, setForm] = useState({});
  const [photos, setPhotos] = useState([]);         // [{data_url, caption, bytes}]
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm({ title: "", description: "", location: "", severity: "medium", assigned_to: "", due_date: "" });
    setPhotos([]);
    setSaving(false);
  }, [open]);

  const pickPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    for (const f of files) {
      if (f.size > 2 * 1024 * 1024) { toast.error(`${f.name} exceeds 2 MB`); continue; }
      if (photos.length >= 12) { toast.error("Max 12 photos"); break; }
      const url = await new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(f); });
      setPhotos((prev) => [...prev, { data_url: url, caption: f.name, bytes: f.size }]);
    }
    e.target.value = "";
  };

  const save = async () => {
    if (!form.title?.trim()) return toast.error("Title required");
    setSaving(true);
    try {
      await api.post(`/projects/${pid}/snags`, {
        ...form,
        assigned_to: form.assigned_to || "",
        due_date: form.due_date || null,
        photos: photos.map((p) => ({ data_url: p.data_url, caption: p.caption })),
      });
      toast.success("Snag logged");
      onSaved && onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to create snag"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-amber-500" /> Log a new snag</DialogTitle>
          <DialogDescription>Capture the defect, location, and photos so the site team can close it out.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Label>Title</Label><Input value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Cracked tile near living room door" data-testid="snag-title" /></div>
          <div className="col-span-2"><Label>Description</Label><Textarea rows={3} value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="What went wrong, what needs to be done" data-testid="snag-desc" /></div>
          <div><Label>Location / room</Label><Input value={form.location || ""} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Master bedroom, kitchen…" data-testid="snag-loc" /></div>
          <div><Label>Severity</Label>
            <Select value={form.severity} onValueChange={(v) => setForm({ ...form, severity: v })}>
              <SelectTrigger data-testid="snag-sev"><SelectValue /></SelectTrigger>
              <SelectContent>{SEVERITIES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Assign to</Label>
            <Select value={form.assigned_to || "__none__"} onValueChange={(v) => setForm({ ...form, assigned_to: v === "__none__" ? "" : v })}>
              <SelectTrigger data-testid="snag-assign"><SelectValue placeholder="Unassigned" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— Unassigned —</SelectItem>
                {members.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Due date</Label><Input type="date" value={form.due_date || ""} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="snag-due" /></div>
          <div className="col-span-2">
            <Label>Photos ({photos.length}/12)</Label>
            <div className="rounded-lg border border-dashed border-border/70 p-3">
              <label className="flex items-center justify-center gap-2 cursor-pointer py-4 rounded-md hover:bg-muted/30 transition" data-testid="snag-photo-picker">
                <ImagePlus className="w-4 h-4 text-primary" />
                <span className="text-xs">Click to add photos (≤ 2 MB each)</span>
                <input type="file" accept="image/*" multiple onChange={pickPhotos} className="hidden" />
              </label>
              {photos.length > 0 && (
                <div className="grid grid-cols-4 gap-2 mt-2">
                  {photos.map((p, i) => (
                    <div key={i} className="relative rounded overflow-hidden border border-border/50 group">
                      <img src={p.data_url} alt={p.caption} className="w-full h-16 object-cover" />
                      <button type="button" onClick={() => setPhotos((prev) => prev.filter((_, x) => x !== i))} className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100">
                        <XIcon className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-2">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-primary text-primary-foreground" data-testid="snag-save">{saving ? "Saving…" : "Log Snag"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------- Detail dialog ----------
function SnagDetailDialog({ open, snagId, pid, members, user, onClose, onChanged }) {
  const [snag, setSnag] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [comment, setComment] = useState("");
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});

  const load = useCallback(async () => {
    if (!snagId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/projects/${pid}/snags/${snagId}`);
      setSnag(data);
      setForm({
        title: data.title || "", description: data.description || "",
        location: data.location || "", severity: data.severity || "medium",
        assigned_to: data.assigned_to || "", due_date: data.due_date || "",
      });
    } catch { toast.error("Failed to load snag"); }
    finally { setLoading(false); }
  }, [snagId, pid]);
  useEffect(() => { if (open) load(); }, [open, load]);

  const patch = async (payload) => {
    if (!snag) return;
    setBusy(true);
    try {
      const { data } = await api.patch(`/projects/${pid}/snags/${snag.id}`, payload);
      setSnag(data);
      onChanged && onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
    finally { setBusy(false); }
  };

  const advance = async () => {
    if (!snag) return;
    const next = STATUS_MAP[snag.status]?.next;
    if (!next) return;
    await patch({ status: next });
    toast.success(`Snag moved to ${STATUS_MAP[next].label}`);
  };
  const revert = async () => {
    if (!snag) return;
    const idx = STATUSES.findIndex((s) => s.key === snag.status);
    if (idx <= 0) return;
    await patch({ status: STATUSES[idx - 1].key });
  };

  const addComment = async () => {
    if (!comment.trim() || !snag) return;
    setBusy(true);
    try {
      await api.post(`/projects/${pid}/snags/${snag.id}/comments`, { text: comment });
      setComment("");
      await load(); onChanged && onChanged();
    } catch { toast.error("Comment failed"); }
    finally { setBusy(false); }
  };

  const uploadPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length || !snag) return;
    setBusy(true);
    try {
      const payload = [];
      for (const f of files) {
        if (f.size > 2 * 1024 * 1024) { toast.error(`${f.name} exceeds 2 MB`); continue; }
        const url = await new Promise((r) => { const rd = new FileReader(); rd.onload = () => r(rd.result); rd.readAsDataURL(f); });
        payload.push({ data_url: url, caption: f.name });
      }
      if (payload.length) {
        await api.post(`/projects/${pid}/snags/${snag.id}/photos`, payload);
        toast.success(`${payload.length} photo${payload.length > 1 ? "s" : ""} added`);
        await load(); onChanged && onChanged();
      }
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); e.target.value = ""; }
  };
  const deletePhoto = async (photoId) => {
    setBusy(true);
    try {
      await api.delete(`/projects/${pid}/snags/${snag.id}/photos/${photoId}`);
      await load();
    } catch { toast.error("Failed to delete photo"); }
    finally { setBusy(false); }
  };

  const saveEdit = async () => {
    await patch(form);
    setEditing(false);
    toast.success("Snag updated");
  };
  const deleteSnag = async () => {
    if (!confirm(`Delete ${snag.snag_no}? This cannot be undone.`)) return;
    setBusy(true);
    try {
      await api.delete(`/projects/${pid}/snags/${snag.id}`);
      toast.success("Snag deleted");
      onChanged && onChanged();
      onClose();
    } catch { toast.error("Delete failed"); }
    finally { setBusy(false); }
  };

  if (!open) return null;
  const overdue = snag && isOverdue(snag);
  const memberOf = (id) => members.find((m) => m.id === id);
  const nextLabel = snag ? STATUS_MAP[STATUS_MAP[snag.status]?.next]?.label : null;
  const canRevert = snag && STATUSES.findIndex((s) => s.key === snag.status) > 0;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
        {loading || !snag ? (
          <div className="text-center py-16 text-muted-foreground">Loading…</div>
        ) : (
          <>
            <DialogHeader>
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="text-[10px] font-mono text-muted-foreground uppercase tracking-widest">{snag.snag_no}</div>
                  {editing
                    ? <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1 text-lg font-semibold" />
                    : <DialogTitle className="text-lg leading-tight">{snag.title}</DialogTitle>}
                  <DialogDescription className="flex items-center flex-wrap gap-2 mt-2">
                    <SeverityPill severity={snag.severity} />
                    <StatusPill status={snag.status} />
                    {overdue && <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded border border-rose-500/40 bg-rose-500/10 text-rose-500">Overdue</span>}
                    {snag.location && <span className="text-xs flex items-center gap-1 text-muted-foreground"><MapPin className="w-3 h-3" />{snag.location}</span>}
                  </DialogDescription>
                </div>
                <div className="flex gap-1">
                  <Button size="sm" variant="ghost" onClick={() => setEditing(!editing)}>{editing ? "Cancel" : "Edit"}</Button>
                  <Button size="sm" variant="ghost" onClick={deleteSnag} className="text-rose-500 hover:text-rose-500"><Trash2 className="w-4 h-4" /></Button>
                </div>
              </div>
            </DialogHeader>

            {/* Status pipeline */}
            <div className="flex items-center gap-1 overflow-x-auto py-3 border-y border-border/40 bg-muted/10 -mx-6 px-6">
              {STATUSES.map((st, i) => {
                const idx = STATUSES.findIndex((s) => s.key === snag.status);
                const done = i < idx;
                const active = i === idx;
                const Icon = st.icon;
                return (
                  <div key={st.key} className="flex items-center gap-1 shrink-0">
                    <button
                      disabled={busy}
                      onClick={() => patch({ status: st.key })}
                      data-testid={`snag-stage-${st.key}`}
                      className={`flex items-center gap-1.5 px-2 py-1 rounded text-[11px] font-medium transition ${active ? "shadow" : ""} ${done ? "text-emerald-600" : active ? "" : "text-muted-foreground hover:text-foreground"}`}
                      style={active ? { background: st.color + "20", color: st.color, borderColor: st.color } : {}}
                    >
                      <Icon className="w-3 h-3" /> {st.label}
                    </button>
                    {i < STATUSES.length - 1 && <span className={`w-4 h-px ${done ? "bg-emerald-500" : "bg-border"}`} />}
                  </div>
                );
              })}
              <div className="ml-auto flex gap-2">
                {canRevert && <Button size="sm" variant="outline" onClick={revert} disabled={busy}>← Back</Button>}
                {nextLabel && <Button size="sm" onClick={advance} disabled={busy} className="bg-primary text-primary-foreground" data-testid="snag-advance"><Check className="w-3.5 h-3.5 mr-1" />Mark {nextLabel}</Button>}
              </div>
            </div>

            {/* Details */}
            <div className="grid grid-cols-2 gap-3 pt-2">
              <div><Label>Location</Label>
                {editing
                  ? <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
                  : <div className="text-sm">{snag.location || "—"}</div>}
              </div>
              <div><Label>Severity</Label>
                {editing
                  ? <Select value={form.severity} onValueChange={(v) => setForm({ ...form, severity: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{SEVERITIES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
                    </Select>
                  : <SeverityPill severity={snag.severity} />}
              </div>
              <div><Label>Assigned to</Label>
                {editing
                  ? <Select value={form.assigned_to || "__none__"} onValueChange={(v) => setForm({ ...form, assigned_to: v === "__none__" ? "" : v })}>
                      <SelectTrigger><SelectValue placeholder="Unassigned" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">— Unassigned —</SelectItem>
                        {members.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  : <div className="text-sm">{memberOf(snag.assigned_to)?.name || "—"}</div>}
              </div>
              <div><Label>Due date</Label>
                {editing
                  ? <Input type="date" value={form.due_date || ""} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
                  : <div className={`text-sm ${overdue ? "text-rose-500 font-medium" : ""}`}>{fmtDate(snag.due_date)}</div>}
              </div>
              <div className="col-span-2"><Label>Description</Label>
                {editing
                  ? <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
                  : <div className="text-sm text-muted-foreground whitespace-pre-wrap">{snag.description || "—"}</div>}
              </div>
              {editing && (
                <div className="col-span-2 flex justify-end">
                  <Button size="sm" onClick={saveEdit} className="bg-primary text-primary-foreground" data-testid="snag-save-edit">Save changes</Button>
                </div>
              )}
              {!editing && (
                <div className="col-span-2 text-[10px] text-muted-foreground border-t border-border/40 pt-2 flex flex-wrap gap-4">
                  {snag.fixed_date    && <span>Fixed · {fmtDate(snag.fixed_date)}</span>}
                  {snag.verified_date && <span>Verified · {fmtDate(snag.verified_date)}</span>}
                  {snag.closed_date   && <span>Closed · {fmtDate(snag.closed_date)}</span>}
                </div>
              )}
            </div>

            {/* Photos */}
            <div className="mt-4 border-t border-border/40 pt-3">
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm font-medium flex items-center gap-1"><Camera className="w-3.5 h-3.5 text-primary" /> Photos · {snag.photos?.length || 0}</div>
                <label className="text-xs cursor-pointer text-primary hover:underline flex items-center gap-1" data-testid="snag-add-photos">
                  <ImagePlus className="w-3.5 h-3.5" /> Add photos
                  <input type="file" accept="image/*" multiple onChange={uploadPhotos} className="hidden" />
                </label>
              </div>
              {snag.photos?.length > 0 ? (
                <div className="grid grid-cols-4 gap-2">
                  {snag.photos.map((p) => (
                    <div key={p.id} className="relative rounded overflow-hidden border border-border/50 group">
                      <a href={p.data_url} target="_blank" rel="noreferrer">
                        <img src={p.data_url} alt={p.caption} className="w-full h-24 object-cover cursor-zoom-in" />
                      </a>
                      <button onClick={() => deletePhoto(p.id)} className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100" data-testid={`snag-photo-del-${p.id}`}>
                        <XIcon className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              ) : <div className="text-xs text-muted-foreground italic">No photos attached.</div>}
            </div>

            {/* Comments */}
            <div className="mt-4 border-t border-border/40 pt-3">
              <div className="text-sm font-medium flex items-center gap-1 mb-2"><MessageSquare className="w-3.5 h-3.5 text-primary" /> Discussion · {snag.comments?.length || 0}</div>
              <div className="space-y-2 mb-2">
                {(snag.comments || []).map((c) => (
                  <div key={c.id} className="rounded-lg border border-border/50 bg-muted/20 px-3 py-2">
                    <div className="text-[10px] text-muted-foreground flex items-center justify-between">
                      <span className="font-medium text-foreground">{c.created_by_name}</span>
                      <span>{new Date(c.created_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
                    </div>
                    <div className="text-xs mt-1 whitespace-pre-wrap">{c.text}</div>
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <Textarea rows={2} value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Add a note or reply…" data-testid="snag-comment-input" className="flex-1" />
                <Button size="sm" onClick={addComment} disabled={!comment.trim() || busy} className="self-end bg-primary text-primary-foreground" data-testid="snag-comment-send">Post</Button>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
